import React, { useState, useRef, useEffect } from "react";
import {
  DownArrowIcon,
  EmptyCurrencyListIcons,
  SearchIcons,
} from "../../Icons";

const CurrenciesInput = ({
  currencies,
  SelectedCurrency,
  onChange,
  placeholder,
  label,
  error,
  children,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchQuery("");
  };

  const filteredCurrencies = currencies
    ? currencies.filter(
        (country) =>
          country.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          country.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : currencies;

  const handleKeyDown = (e, countryItem) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        handleSelect(countryItem);
        break;
      case "ArrowDown":
        e.preventDefault();
        const nextElement = e.target.nextElementSibling;
        if (nextElement) {
          nextElement.focus();
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        const prevElement = e.target.previousElementSibling;
        if (prevElement) {
          prevElement.focus();
        }
        break;
      default:
        break;
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown" && e.target === searchInputRef.current) {
      const firstElement =
        dropdownRef.current.querySelector("li[tabindex='0']");
      if (firstElement) {
        firstElement.focus();
      }
    }
  };

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        tabIndex={0}
        onKeyDown={() => setIsOpen(!isOpen)}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full cursor-pointer shadow-sm rounded-xl flex justify-between items-center py-2.5 px-3 select-input-wrapper
          ${error ? "select-input-error" : "select-input-default"}`}
      >
        {SelectedCurrency ? (
          children
        ) : (
          <div className="flex justify-between w-full items-center">
            <div className="space-y-1">
              <p className={"block text-xs font-medium select-input-lable"}>
                {label}
              </p>
              <p className="select-input-placeholder">{placeholder}</p>
            </div>
            <DownArrowIcon className="w-4 h-4 mr-2" />
          </div>
        )}
      </div>

      {/* Dropdown with transition */}
      <div
        className={`absolute z-10 w-[340px] md:w-[360px] mt-1 select-input-dropdow-wrapper
          ${
            isOpen
              ? "opacity-100 scale-100 pointer-events-auto"
              : "opacity-0 scale-95 pointer-events-none"
          }
        `}
        style={{ left: 0 }}
      >
        {isOpen && (
          <ul className="flex flex-col space-y-4 max-h-[260px] hide-scrollbar overflow-scroll h-auto bg-background-surface border border-divider rounded-xl shadow-box-shadow p-2">
            <li>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (filteredCurrencies.length > 0) {
                    handleSelect(filteredCurrencies[0]);
                  }
                  return;
                }}
              >
                <div className="max-w-full px-3 py-1 search-input-wrapper rounded-full flex justify-start items-center">
                  <SearchIcons className="w-6 h-6 relative" />
                  <input
                    ref={searchInputRef}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    value={searchQuery}
                    placeholder="Search"
                    autoFocus={true}
                    className="w-full select-input-search-input"
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
              </form>
            </li>

            {filteredCurrencies.length === 0 ? (
              <li className="flex flex-col items-center py-6 justify-center text-center text-sm text-text-primary">
                <EmptyCurrencyListIcons className="h-20 w-20" />
                <p>No currencies found</p>
              </li>
            ) : (
              filteredCurrencies.map((currencyItem) => (
                <li
                  tabIndex={0}
                  key={currencyItem.id}
                  className={`${
                    currencyItem.id === SelectedCurrency?.id &&
                    "select-input-item-selected"
                  } flex justify-between select-input-item`}
                  onClick={() => handleSelect(currencyItem)}
                  onKeyDown={(e) => handleKeyDown(e, currencyItem)}
                >
                  <div className="flex space-x-3 items-center">
                    <img
                      className="rounded-full w-8 h-8"
                      src={currencyItem.logo}
                      alt="logo"
                    />
                    <span>
                      {currencyItem.name} {currencyItem.code}
                    </span>
                  </div>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CurrenciesInput;
